import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Fab,
  Typography,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Collapse,
} from '@mui/material';
import { debounce } from 'lodash';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomMap from './MapLibre';
import WaypointMarker from './icons/WaypointMarkerIcon';
import SwapIcon from './icons/SwapIcon';
import HidePointsIcon from './icons/HidePointsIcon';
import StartLocationIcon from './icons/StartLocationIcon';
import EndLocationIcon from './icons/EndLocationIcon';
import ArrowAltRightIcon from './icons/ArrowAltRightIcon';
import ArrowAltLeftIcon from './icons/ArrowAltLeftIcon';
import {
  useCreateSavedRoute,
  useUpdateSavedRoute,
} from '@/hooks/admin/useSavedRoutes';
import { useConfigurations } from '@/hooks/admin/useConfigurations';
import { useNZTA } from '@/hooks/admin/useNzta';
import { use50Max } from '@/hooks/admin/use50Max';
import { useHPMV } from '@/hooks/admin/useHpmv';
import { useVehicles } from '@/hooks/admin/useVehicles';
import { useColorMode } from '@/styles/theme';
import { useLocation } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
interface Waypoint {
  name?: string;
  coordinates: [number, number];
}
interface RouteData {
  _id?: string;
  start?: {
    name: string;
    coordinates: [number, number];
  } | null;
  end?: {
    name: string;
    coordinates: [number, number];
  } | null;
  waypoints?: Waypoint[];
  geojsonData: any[];
}
interface Suggestion {
  id: string;
  place_name: string;
  center: [number, number];
}

/**
 * The component can optionally receive a pre-existing RouteData object
 * to re-render a saved route (routeData?), plus other props controlling UI.
 *
 * If you only want to pass in geojson data and not a saved route,
 * you can omit routeData.
 */
interface MapStadiaWithAutocompleteProps {
  /**
   * If provided, your component can initialize from this saved route object.
   */
  routeData?: RouteData;

  show50MaxData?: boolean;

  showHpmvData?: boolean;

  company: string;
  configuration: string;

  /**
   * Whether or not the side bar is displayed and a function to toggle it.
   */
  displayLeftBar: boolean;
  switchLeftBar: () => void;
  
  isRouteLoading?: boolean;
}

const generateRandomSuffix = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const MapStadiaWithAutocomplete: React.FC<MapStadiaWithAutocompleteProps> = ({
  routeData,
  show50MaxData = false,
  showHpmvData = false,
  configuration,
  company,
  displayLeftBar,
  switchLeftBar,
  isRouteLoading = false,
}) => {
  const { mode } = useColorMode();
  const location = useLocation();
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [startSuggestions, setStartSuggestions] = useState<Suggestion[]>([]);
  const [endSuggestions, setEndSuggestions] = useState<Suggestion[]>([]);
  const [startPoint, setStartPoint] = useState<[number, number] | null>(null);
  const [endPoint, setEndPoint] = useState<[number, number] | null>(null);
  const [waypoints, setWaypoints] = useState<[number, number][]>([]);
  const [isWaypointMode, setIsWaypointMode] = useState(false);
  const [startValue, setStartValue] = useState<Suggestion | null>(null);
  const [endValue, setEndValue] = useState<Suggestion | null>(null);
  const [showHighRiskOnly, setShowHighRiskOnly] = useState(false);
  const [totalDistance, setTotalDistance] = useState(0);
  const [tilesetIds, setTilesetIds] = useState<string[]>([]);
  const [rcaTilesetIds, setRcaTilesetIds] = useState<string[]>([]);
  const [fiftyMaxGeojson, setFiftyMaxGeojson] = useState<any[]>();
  const [hpmvGeojson, setHpmvGeojson] = useState<any[]>();
  const [geojsonArray, setGeojsonArray] = useState<any[]>([]);
  const [startLoading, setStartLoading] = useState(false);
  const [endLoading, setEndLoading] = useState(false);
  const [selectedRegionalCouncils, setSelectedRegionalCouncils] = useState<
    { name: string; hasGeojsonData: boolean; hasUploads: boolean }[]
  >([]);
  const [selectedTerritorialAuthorities, setSelectedTerritorialAuthorities] =
    useState<string[]>([]);
  const [showRegionalCouncils, setShowRegionalCouncils] = useState(false);
  const [showTerritorialAuthorities, setShowTerritorialAuthorities] =
    useState(false);

  const createSavedRoute = useCreateSavedRoute();
  const updateSavedRoute = useUpdateSavedRoute();
  const { data: configurations } = useConfigurations();
  const { data: nztaData, isLoading: isLoadingNztaData } = useNZTA();
  const { data: fiftyMaxData, isLoading: isLoadingFiftyMaxData } = use50Max();
  const { data: hpmvData, isLoading: isLoadingHpmvData } = useHPMV();
  const isOnSavedRoutesPage = location.pathname.includes('/saved-routes');

  const hasGeojsonData = useMemo(() => {
    return (
      geojsonArray &&
      geojsonArray.length > 0 &&
      geojsonArray.some((fc) => fc && fc.features && fc.features.length > 0)
    );
  }, [geojsonArray]);

  const isLoading =
    isLoadingNztaData || isLoadingFiftyMaxData || isLoadingHpmvData;

  useEffect(() => {
    if (routeData) {
      const { start, end, waypoints, geojsonData } = routeData;

      if (start) {
        setStartInput(start.name);
        setStartPoint(start.coordinates);
        setStartValue({
          id: 'start',
          place_name: start.name,
          center: start.coordinates,
        });
      }

      if (end) {
        setEndInput(end.name);
        setEndPoint(end.coordinates);
        setEndValue({
          id: 'end',
          place_name: end.name,
          center: end.coordinates,
        });
      }

      if (waypoints) {
        setWaypoints(waypoints.map((wp) => wp.coordinates));
      }

      if (geojsonData) {
        setGeojsonArray(geojsonData);
      }
    }
  }, [routeData]);

  useEffect(() => {
    if (!nztaData || !configuration) return;

    const currentConfiguration = configurations?.find(
      (c) => c._id === configuration,
    );

    if (
      !currentConfiguration?.permits ||
      !Array.isArray(currentConfiguration.permits)
    ) {
      return;
    }

    const validPermits = currentConfiguration.permits.filter((permit) => {
      if (!permit.expiryDate) {
        return true;
      }

      const expiryDate = new Date(permit.expiryDate);
      const currentDate = new Date();

      // Zero out the time part to compare only dates
      expiryDate.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);

      return expiryDate >= currentDate;
    });

    // Calculate selected regional councils (from permits with councils)
    setSelectedRegionalCouncils(() => {
      const regionalCouncilsWithData: Array<{
        name: string;
        hasGeojsonData: boolean;
        hasUploads: boolean;
      }> = [];

      validPermits.forEach((permit, permitIndex) => {
        if (permit.councils?.length) {
          const nztaItem = nztaData.find((ta) => permit.rca === ta._id);
          if (nztaItem?.councils) {
            nztaItem.councils
              .filter((council) =>
                permit.councils.find((c) => council._id === c._id),
              )
              .forEach((council, councilIndex) => {
                if (council.name) {
                  let hasData;
                  if (permit.localCouncil) {
                    const geojsonIndex =
                      permitIndex * (permit.councils?.length || 0) +
                      councilIndex;

                    hasData =
                      geojsonArray[geojsonIndex] &&
                      geojsonArray[geojsonIndex].features.length > 0;
                  } else {
                    hasData = hasData =
                      geojsonArray.length > 0 &&
                      !geojsonArray.every((value) => {
                        return value === null || value === undefined;
                      });
                  }

                  regionalCouncilsWithData.push({
                    name: council.name,
                    hasGeojsonData: hasData,
                    hasUploads: permit.uploads.length > 0,
                  });
                }
              });
          }
        }
      });

      if (regionalCouncilsWithData.length > 0) {
        setShowRegionalCouncils(true);
      }

      // Remove duplicates by name, keeping the one with data if any exists
      const uniqueCouncils = regionalCouncilsWithData.reduce(
        (acc, current) => {
          const existing = acc.find((item) => item.name === current.name);
          if (!existing) {
            acc.push(current);
          } else if (current.hasGeojsonData && !existing.hasGeojsonData) {
            // Replace with the one that has data
            const index = acc.findIndex((item) => item.name === current.name);
            acc[index] = current;
          }
          return acc;
        },
        [] as Array<{
          name: string;
          hasGeojsonData: boolean;
          hasUploads: boolean;
        }>,
      );

      return uniqueCouncils;
    });

    // Calculate all territorial authority names (all councils from all NZTA items in current config)
    setSelectedTerritorialAuthorities(() => {
      const territorialAuthorityNames: string[] = [];

      validPermits.forEach((permit) => {
        if (permit?.councils?.length && permit.localCouncil) {
          return;
        }

        const nztaItem = nztaData.find((ta) => permit.rca === ta._id);
        if (nztaItem?.councils) {
          nztaItem.councils.forEach((council) => {
            if (council.name) {
              territorialAuthorityNames.push(council.name);
            }
          });
        }
      });

      if (territorialAuthorityNames.length > 0) {
        setShowTerritorialAuthorities(true);
      }

      return [...new Set(territorialAuthorityNames)];
    });

    setTilesetIds(() => {
      const tilesetIds: string[] = [];

      validPermits.forEach((permit) => {
        if (!permit.localCouncil) {
          return;
        }

        if (permit.councils?.length) {
          const nztaItem = nztaData.find((ta) => permit.rca === ta._id);
          if (nztaItem?.councils) {
            nztaItem.councils
              .filter((council) =>
                permit.councils.find((c) => council._id === c._id),
              )
              .forEach((council) => {
                tilesetIds.push(council.tilesetId);
              });
          }
        }
      });

      const uniqueTilesetIds = [...new Set(tilesetIds)];
      return uniqueTilesetIds;
    });

    setRcaTilesetIds(() => {
      const rcaTilesetIds: string[] = [];

      validPermits.forEach((permit) => {
        if (permit.localCouncil) {
          return;
        }

        const nztaItem = nztaData.find((ta) => permit.rca === ta._id);
        if (nztaItem?.tilesetId) {
          rcaTilesetIds.push(nztaItem?.tilesetId);
          if (nztaItem?._id === '67d257aa6659124f0e5df0b1') {
            // todo: this should be integrated into the tileset
            rcaTilesetIds.push('orchidweb.nzta_gisborne_ha_sborne_district');
          }
        }
      });

      const uniqueRcaTilesetIds = [...new Set(rcaTilesetIds)];
      return uniqueRcaTilesetIds;
    });

    setFiftyMaxGeojson(() => {
      if (!show50MaxData) {
        return [];
      }

      return fiftyMaxData;
    });

    setHpmvGeojson(() => {
      if (!showHpmvData) {
        return [];
      }

      return hpmvData;
    });
  }, [
    nztaData,
    configuration,
    configurations,
    fiftyMaxData,
    hpmvData,
    geojsonArray,
  ]);

  const fetchSuggestions = async (
    input: string,
    setter: React.Dispatch<React.SetStateAction<Suggestion[]>>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!input) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Stadia Maps (Pelias) geocoding API
    const url = `/api/v1/customer/map-geocoding/v1?text=${encodeURIComponent(
      input,
    )}&size=5&boundary.country=NZ`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (!data?.features) return;
      const suggestions: Suggestion[] = data.features.map((feature: any) => ({
        id: feature.properties?.id || feature.properties?.gid || feature.id,
        place_name:
          feature.properties?.label || feature.properties?.name || 'Unknown',
        center: feature.geometry?.coordinates,
      }));

      setter(suggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchSuggestionsStart = useCallback(
    debounce((value: string) => {
      fetchSuggestions(value, setStartSuggestions, setStartLoading);
    }, 500),
    [],
  );

  const debouncedFetchSuggestionsEnd = useCallback(
    debounce((value: string) => {
      fetchSuggestions(value, setEndSuggestions, setEndLoading);
    }, 500),
    [],
  );

  const toggleWaypointMode = () => {
    setIsWaypointMode(!isWaypointMode);
  };

  const handleSaveRoute = useCallback(async () => {
    const routeToSave = {
      start: startPoint
        ? {
            name: startInput,
            coordinates: startPoint,
          }
        : null,
      end: endPoint
        ? {
            name: endInput,
            coordinates: endPoint,
          }
        : null,
      waypoints: waypoints.map((coords) => ({
        coordinates: coords,
      })),
      configuration,
      company,
    };

    createSavedRoute.mutate(routeToSave);
  }, [startInput, endInput, startPoint, endPoint, waypoints, geojsonArray]);

  const handleSaveAsNew = useCallback(async () => {
    if (!startPoint || !endPoint) return;

    const randomSuffix = generateRandomSuffix();
    const newRouteName = `${startInput} - ${endInput} (${randomSuffix})`;

    const routeToSave = {
      start: {
        name: startInput,
        coordinates: startPoint,
      },
      end: {
        name: endInput,
        coordinates: endPoint,
      },
      waypoints: waypoints.map((coords) => ({
        coordinates: coords,
      })),
      configuration,
      company,
    };

    createSavedRoute.mutate(routeToSave);
  }, [
    startInput,
    endInput,
    startPoint,
    endPoint,
    waypoints,
    configuration,
    company,
    createSavedRoute,
  ]);

  const handleUpdateSaveRoute = useCallback(async () => {
    const routeToUpdate = {
      _id: routeData?._id,
      start: startPoint
        ? {
            name: startInput,
            coordinates: startPoint,
          }
        : null,
      end: endPoint
        ? {
            name: endInput,
            coordinates: endPoint,
          }
        : null,
      waypoints: waypoints.map((coords) => ({
        coordinates: coords,
      })),
      configuration,
      company,
    };

    updateSavedRoute.mutate(routeToUpdate);
  }, [startInput, endInput, startPoint, endPoint, waypoints, geojsonArray]);

  useEffect(() => {
    const fetchRouteDistance = async () => {
      if (!startPoint || !endPoint) {
        setTotalDistance(0);
        return;
      }
      const chain = [startPoint, ...waypoints, endPoint];
      const body = {
        locations: chain.map(([lng, lat]) => ({
          lon: lng,
          lat,
          type: 'break',
        })),
        costing: 'auto',
        units: 'kilometers',
        shape_format: 'polyline6',
      } as any;
      try {
        const res = await fetch('/api/v1/customer/map-route/v1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          console.error(
            'Route distance HTTP error',
            res.status,
            await res.text(),
          );
          setTotalDistance(0);
          return;
        }
        const data = await res.json();
        const km = data?.trip?.summary?.length; // length already in kilometers because units=kms
        if (typeof km === 'number') setTotalDistance(km);
        else setTotalDistance(0);
      } catch (e) {
        console.error('Valhalla distance error', e);
        setTotalDistance(0);
      }
    };
    fetchRouteDistance();
  }, [startPoint, endPoint, waypoints]);

  if (isLoading) {
    return (
      <Box>
        <CircularProgress size="small" />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
        marginTop={2}
        gap={2}
        width={'100%'}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: (theme) => theme.zIndex.modal + 1,
          pointerEvents: 'auto',
          // optional backdrop styling for readability
          // background: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0))',
          marginTop: '20px',
          width: displayLeftBar ? '95%' : '95%',
          paddingLeft: displayLeftBar ? '20px' : '5%',
        }}
      >
        <Autocomplete<Suggestion>
          options={startSuggestions}
          getOptionLabel={(option) => option.place_name}
          filterOptions={(x) => x}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          inputValue={startInput}
          value={startValue}
          onInputChange={(_event, value) => {
            setStartInput(value);
            if (value.length > 2) {
              debouncedFetchSuggestionsStart(value);
            }
          }}
          onChange={(_event: any, value: Suggestion | null) => {
            if (value) {
              setStartPoint(value.center);
            } else {
              setStartPoint(null);
            }
            setStartValue(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box component="div" display="flex" justifyContent="start">
                  <StartLocationIcon mode={mode} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      ml: 2,
                      color: mode === 'dark' ? '#fff' : '#202D38',
                    }}
                  >
                    Start Location
                  </Typography>
                </Box>
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {startLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              variant="filled"
              fullWidth
              sx={{
                color: '#fff',
                '& .MuiFilledInput-root': {
                  background:
                    mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.09) !important'
                      : 'rgba(25, 25, 25, 0.15) !important',
                  color:
                    mode === 'dark' ? '#fff !important' : '#202D38 !important',
                  '&:hover': {
                    background:
                      mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.13) !important'
                        : 'rgba(25, 25, 25, 0.20) !important',
                  },
                  '&.Mui-focused': {
                    background: 'rgba(255, 255, 255, 0.09)',
                  },
                },
              }}
            />
          )}
          style={{ width: '45%' }}
        />

        <IconButton
          sx={{ marginTop: '25px' }}
          onClick={() => {
            const tempStartPoint = startPoint;
            const tempStartInput = startInput;
            const tempStartValue = startValue;

            setStartPoint(endPoint);
            setStartInput(endInput);
            setStartValue(endValue);

            setEndPoint(tempStartPoint);
            setEndInput(tempStartInput);
            setEndValue(tempStartValue);
          }}
        >
          <SwapIcon />
        </IconButton>

        <Autocomplete<Suggestion>
          options={endSuggestions}
          getOptionLabel={(option) => option.place_name}
          filterOptions={(x) => x}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          inputValue={endInput}
          value={endValue}
          onInputChange={(_event, value) => {
            setEndInput(value);
            if (value.length > 2) {
              debouncedFetchSuggestionsEnd(value);
            }
          }}
          onChange={(_event: any, value: Suggestion | null) => {
            if (value) {
              setEndPoint(value.center);
            } else {
              setEndPoint(null);
            }
            setEndValue(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box component="div" display="flex" justifyContent="start">
                  <EndLocationIcon />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      ml: 2,
                      color: mode === 'dark' ? '#fff' : '#202D38',
                    }}
                  >
                    End Location
                  </Typography>
                </Box>
              }
              variant="filled"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {endLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                color: '#fff',
                '& .MuiFilledInput-root': {
                  background:
                    mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.09) !important'
                      : 'rgba(25, 25, 25, 0.15) !important',
                  color:
                    mode === 'dark' ? '#fff !important' : '#202D38 !important',
                  '&:hover': {
                    background:
                      mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.13) !important'
                        : 'rgba(25, 25, 25, 0.20) !important',
                  },
                  '&.Mui-focused': {
                    background: 'rgba(255, 255, 255, 0.09)',
                  },
                },
              }}
              fullWidth
            />
          )}
          style={{ width: '45%' }}
        />

        <Fab
          variant="circular"
          onClick={switchLeftBar}
          sx={{
            backgroundColor: mode === 'dark' ? '#121212' : '#FFF',
            position: 'absolute',
            left: '0px',
            top: '40vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 1,
            borderRadius: 0,
            borderTopRightRadius: '50%',
            borderBottomRightRadius: '50%',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#121212' : '#FFF',
              boxShadow: 'none',
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              backgroundColor: mode === 'dark' ? '#222' : '#EEE',
              borderRadius: '50%',
              width: '45px',
              height: '45px',
            }}
          >
            {displayLeftBar && <ArrowAltLeftIcon color="#F6AE2D" />}
            {!displayLeftBar && <ArrowAltRightIcon color="#F6AE2D" />}
          </Box>
        </Fab>

        <Fab
          variant="circular"
          onClick={toggleWaypointMode}
          sx={{
            backgroundColor: 'primary.main',
            position: 'absolute',
            left: '10px',
            top: '50vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center">
            <WaypointMarker
              style={
                isWaypointMode ? { position: 'relative', left: '4px' } : {}
              }
            />
            {isWaypointMode ? (
              <Typography
                variant="caption"
                style={{
                  color: '#000',
                  fontSize: '16px',
                  marginTop: '-25px',
                  textAlign: 'center',
                }}
              >
                +
              </Typography>
            ) : null}
          </Box>
        </Fab>

        <Fab
          variant="circular"
          onClick={() => setShowHighRiskOnly(!showHighRiskOnly)}
          sx={{
            backgroundColor: 'secondary',
            position: 'absolute',
            left: '10px',
            top: '60vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center">
            <HidePointsIcon
              circleStroke={showHighRiskOnly ? 'white' : 'black'}
            />
          </Box>
        </Fab>

        <Fab
          variant="circular"
          onClick={routeData?._id ? handleUpdateSaveRoute : handleSaveRoute}
          sx={{
            backgroundColor: 'primary.main',
            position: 'absolute',
            left: '10px',
            top: '70vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SaveIcon />
        </Fab>

        {isOnSavedRoutesPage && routeData?._id && (
          <Fab
            variant="circular"
            onClick={handleSaveAsNew}
            sx={{
              backgroundColor: 'secondary',
              position: 'absolute',
              left: '10px',
              top: '80vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ContentCopyIcon />
          </Fab>
        )}
      </Box>

      <DetailsCard
        configurationId={configuration}
        totalDistance={totalDistance}
      />

      <CustomMap
        geojsonData={geojsonArray}
        fiftyMaxData={fiftyMaxGeojson || []}
        hpmvData={hpmvGeojson || []}
        tilesetIds={tilesetIds}
        rcaTilesetIds={rcaTilesetIds}
        councils={[]} // You can populate this if needed for other purposes
        selectedRegionalCouncils={selectedRegionalCouncils}
        selectedTerritorialAuthorities={selectedTerritorialAuthorities}
        showRegionalCouncils={showRegionalCouncils}
        showTerritorialAuthorities={showTerritorialAuthorities}
        startPoint={startPoint}
        endPoint={endPoint}
        waypoints={waypoints}
        setWaypoints={setWaypoints}
        isWaypointMode={isWaypointMode}
        showHighRiskOnly={showHighRiskOnly}
        displayLeftBar={displayLeftBar}
        hasGeojsonData={hasGeojsonData}
      />

      {isRouteLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            pointerEvents: 'auto', // block map interaction while loading
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '999px',
              bgcolor: 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <CircularProgress size={28} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DetailsCard = ({
  configurationId,
  totalDistance,
}: {
  configurationId: string;
  totalDistance: number;
}) => {
  const { mode } = useColorMode();
  const { data: vehicleData, isLoading: isLoadingVehicleData } = useVehicles();
  const { data: configurations } = useConfigurations();
  const [cardExpanded, setCardExpanded] = useState(false);

  const detailLists = useMemo(() => {
    if (!configurations || !vehicleData) return [];
    const configuration = configurations.find((c) => c._id === configurationId);
    const vehicle = vehicleData.find(
      (vehicle) => vehicle._id === configuration?.vehicle,
    );

    return [
      {
        title: 'VEHICLE',
        text:
          vehicle?.fleetNumber && configuration?.name
            ? `${vehicle?.fleetNumber}, ${configuration?.name}`
            : 'N/A',
      },
      {
        title: 'ROUTE DISTANCE',
        text: `${totalDistance.toFixed(2)} km`,
      },
      // {
      //   title: 'ESTIMATED RUC',
      //   text: '$0000.00',
      // },
    ];
  }, [configurations, vehicleData, totalDistance]);

  const handleCardExpand = () => {
    setCardExpanded((prev) => !prev);
  };

  if (isLoadingVehicleData || !vehicleData || !configurations) return <></>;

  return (
    <Box
      sx={{
        maxWidth: '200px',
        position: 'absolute',
        bottom: '10%',
        right: '3%',
        zIndex: 100000,
      }}
    >
      <CardActions
        disableSpacing
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
        }}
      >
        <IconButton
          onClick={handleCardExpand}
          sx={{
            padding: 0,
            backgroundColor: '#546472',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#7b8b9a',
              svg: {
                fill: '#fff',
              },
            },
          }}
        >
          <ExpandMoreIcon
            sx={{
              fill: '#B4BBC1',
              transform: cardExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </IconButton>
      </CardActions>
      <Card
        sx={{
          border: '1.5px solid #F6AE2D',
          borderRadius: '8px',
          minWidth: 200,
        }}
      >
        <CardContent
          sx={{
            paddingBottom: '16px !important',
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                lineHeight: '18px',
                textTransform: 'uppercase',
                color: '#F6AE2D',
              }}
            >
              {detailLists[0].title}:
            </Box>
            <Box
              sx={{
                fontWeight: 400,
                lineHeight: '22px',
                fontSize: '16px',
              }}
              color={mode === 'dark' ? '#F5F7F8' : '#202D38'}
            >
              {detailLists[0].text}
            </Box>
          </Box>
          <Collapse in={cardExpanded} timeout="auto" unmountOnExit>
            <Box>
              {detailLists.map((detail, i) => {
                if (i === 0) return;
                return (
                  <Box key={`detailLists-${i}`}>
                    <Box
                      sx={{
                        mt: 1.5,
                        fontSize: '12px',
                        fontWeight: '600',
                        letterSpacing: '1px',
                        lineHeight: '18px',
                        textTransform: 'uppercase',
                        color: '#F6AE2D',
                      }}
                    >
                      {detail.title}
                    </Box>
                    <Box
                      sx={{
                        fontWeight: 400,
                        lineHeight: '22px',
                        fontSize: '16px',
                      }}
                      color={mode === 'dark' ? '#F5F7F8' : '#202D38'}
                    >
                      {detail.text.split('\n').map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MapStadiaWithAutocomplete;
